import { useEffect, useState } from 'react';
import {
  DefaultGenerics,
  ExtendableGenerics,
  OwnUserResponse,
  StreamChat,
  TokenOrProvider,
  UserResponse,
} from 'stream-chat';

export const useConnectUser = <SCG extends ExtendableGenerics = DefaultGenerics>(
  apiKey: string,
  userToConnect: OwnUserResponse<SCG> | UserResponse<SCG>,
  userTokenOrProvider: TokenOrProvider,
) => {
  /**
   * Stores the connected Stream Chat client instance
   * INITIAL VALUE: null (not connected yet)
   * UPDATED: After successful user connection
   */
  const [chatClient, setChatClient] = useState<StreamChat<SCG> | null>(null);
  
  /**
   * STATE: connectError
   * Stores any error that occurs during connection
   * INITIAL VALUE: null (no error)
   * UPDATED: If connection fails
   */
  const [connectError, setConnectError] = useState<Error | null>(null);

  useEffect(() => {
    /**
     * CREATE CLIENT INSTANCE
     * Uses getInstance to get singleton Stream Chat client
     * OPTIONS:
     * - enableInsights: true - Enable analytics and insights
     * - enableWSFallback: true - Fall back to HTTP if WebSocket fails
     */
    const client = StreamChat.getInstance<SCG>(apiKey, {
      enableInsights: true,
      enableWSFallback: true,
    });

    /**
     * CLEANUP FLAG: didCancel
     * Prevents setting state after component unmounts
     * Set to true in cleanup function
     * Checked before setChatClient to avoid memory leaks
     */
    let didCancel = false;
    
    /**
     * PROMISE REFERENCE: connectPromise
     * Stores connection promise so cleanup can wait for it
     * Needed to properly disconnect user if connection is still in progress
     */
    let connectPromise: Promise<void>;

    /**
     * ASYNC FUNCTION: connectUser
     * Handles the actual user connection to Stream Chat
     * FLOW:
     * 1. Call client.connectUser with user data and auth token
     * 2. Wait for connection to complete
     * 3. If component still mounted (didCancel = false) → set chatClient state
     * 4. On error → log error and set connectError state
     * 
     * ERROR HANDLING:
     * Catches connection errors and stores in state for app to handle
     */
    const connectUser = async () => {
      try {
        // Connect user with authentication
        connectPromise = client.connectUser(userToConnect, userTokenOrProvider) as Promise<void>;
        await connectPromise;

        // Only update state if component is still mounted
        if (!didCancel) {
          setChatClient(client);
        }
      } catch (err) {
        console.log('Failed to disconnect user', err);
        setConnectError(err as Error);
      }
    };

    // Start connection immediately
    connectUser();

    /**
     * CLEANUP FUNCTION
     * Runs when component unmounts or dependencies change
     * RESPONSIBILITIES:
     * 1. Set didCancel flag to prevent state updates after unmount
     * 2. Wait for connection promise to complete (if still pending)
     * 3. Disconnect user from Stream Chat
     * 4. Reset chatClient state to null
     * 
     * WHY WAIT FOR connectPromise:
     * - Can't disconnect until connection completes
     * - Prevents "disconnect before connect" errors
     * - Ensures clean shutdown
     */
    return () => {
      didCancel = true;
      connectPromise
        ?.then(() => client.disconnectUser())
        .catch((e) => {
          console.log('Failed to disconnect user', e);
        });

      setChatClient(null);
    };
  }, []);

  /**
   * RETURN VALUES
   * chatClient: Connected client instance (null until ready)
   * connectError: Any error that occurred during connection
   * USAGE IN COMPONENTS:
   * - Check if chatClient exists before rendering chat UI
   * - Show error message if connectError exists
   * - Show loading spinner while both are null
   */
  return { chatClient, connectError };
};

